<template>
  <global-modal name="chat-modal" ref="modal" @before-open="beforeOpen">
    <Modal @close="closeModal()">
      <section class="chat-modal">
        <span v-if="loading" class="page-loading">
          {{ $t("services.loading") }}
        </span>
        <template v-else>
          <div class="chat-modal__body">
            <Table
              :columns="columns"
              @sort="sortBy = $event"
              :rows="[order]"
              :sortBy="sortBy"
              class="chat-modal-table"
            >
              <template #td="{ row, col, i, clickHandler }">
                <td v-if="col.label === 'action'" width="50">
                  <button
                    type="button"
                    class="table__open"
                    @click="clickHandler(i)"
                  >
                    <icon name="arrow-down" />
                  </button>
                </td>
                <td v-else-if="col.label === 'status'">
                  <div class="orders-page-table__status">
                    <span :class="getStatusColor(row.status)">
                      {{ getStatus(row.status) | localeField("title") }}
                    </span>
                  </div>
                </td>
                <td v-else-if="col.label === 'summ'" class="align-right">
                  {{ getSumm(row) | formatPrice({ currency: row.currency }) }}
                </td>
                <td v-else>
                  <span>
                    {{ row[col.label] ? row[col.label] : "-" }}
                  </span>
                </td>
              </template>
              <template #m-row="{ row, columns, i}">
                <td
                  :class="{ 'active-left': popularVisible === i }"
                  :colspan="columns?.length"
                >
                  <div class="chat-modal-table__form">
                    <div
                      class="chat-modal-table__form-row"
                      v-for="(col, i) in columns.filter(c => !c.showMobile)"
                      :key="i"
                    >
                      <template v-if="col.label === 'status'">
                        <span>{{ col | localeField("title") }}</span>
                        <div class="orders-page-table__status">
                          <span :class="getStatusColor(row.status)">
                            {{ getStatus(row.status) | localeField("title") }}
                          </span>
                        </div>
                      </template>
                      <template v-else>
                        <span>{{ col | localeField("title") }}</span>
                        <div>{{ row[col.label] ? row[col.label] : "-" }}</div>
                      </template>
                    </div>
                  </div>
                </td>
              </template>
            </Table>
            <div class="chat-modal__container" ref="messages">
              <template v-for="(date, i) in messages">
                <div class="chat-modal__divider" :key="i">
                  <hr />
                  <span>{{ formatDate(date.date) }}</span>
                </div>
                <template v-for="(message, j) in date.messages">
                  <div
                    v-if="displayMessage(message)"
                    :key="`${i}${j}message`"
                    class="chat-modal__messages"
                    :class="{
                      'chat-modal__messages--right': message.from !== 'manager'
                    }"
                  >
                    <div
                      class="chat-modal__messages-avatar"
                      v-if="message.from === 'manager' && message.manager.image"
                    >
                      <img
                        :src="message.manager.image.thumbnail_path | image"
                        :alt="getPersonName(message)"
                      />
                    </div>
                    <div class="chat-modal__messages-container">
                      <span
                        class="chat-modal__messages-header"
                        :class="{
                          'chat-modal__messages-header--right':
                            message.from !== 'manager'
                        }"
                      >
                        <span class="chat-modal__messages-time">
                          {{ getTime(message.created_at) }}</span
                        >
                        <span class="chat-modal__messages-name">
                          {{ getPersonName(message) }}
                        </span>
                      </span>
                      <div
                        class="chat-modal__messages-list"
                        :class="{
                          'chat-modal__messages-list--right':
                            message.from !== 'manager'
                        }"
                      >
                        <div
                          v-if="message.text"
                          class="chat-modal__message"
                          :class="{
                            'chat-modal__message--light':
                              message.from === 'manager'
                          }"
                        >
                          <span>{{ message.text }}</span>
                        </div>
                        <div v-if="message.files?.length">
                          <div
                            v-for="file in message.files"
                            :key="file.id"
                            class="chat-modal__message chat-modal__message--file"
                            :class="{ disabled: isDocLoading }"
                            @click="downloadFile(file)"
                          >
                            <icon name="attachment" />
                            <div>
                              <span>{{ file.original_name }}</span>
                              <p>{{ attachmentInfo(file) }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </div>
          <span class="chat-modal__advice-message">
            {{ $t("services.chat.advice") }}
          </span>
          <div class="chat-modal__message-form">
            <ul class="chat-modal__attached-files">
              <li
                v-for="(file, i) in files"
                :key="i"
                class=" chat-modal__message--file"
              >
                <span>{{ file.name }}</span>
                <div @click="removeFile(file)">
                  <icon name="close" />
                </div>
              </li>
            </ul>
            <form class="chat-modal__form" @submit.prevent="sendMessage">
              <label class="chat-modal__attach">
                <icon name="attachment" />
                <input type="file" @change="loadFile" multiple ref="myFiles" />
              </label>

              <textarea
                class="chat-modal__message-input"
                ref="messageInput"
                :placeholder="$t('services.chat.inputPlaceholder')"
                v-model.trim="message"
                @input="resizeTextArea"
                @keydown="handleKeyDown"
              ></textarea>
              <PrimaryButton
                class="chat-modal__message-input__button"
                type="sumbmit"
                :disabled="isMessageSending"
                :is-loading="isMessageSending"
              >
                <icon name="send-plane" v-if="!isMessageSending" />
                <span>Отправить</span>
              </PrimaryButton>
            </form>
          </div>
        </template>
      </section>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "@/components/Modal/Modal.vue";
import { PrimaryButton, Table } from "@v2/shared/ui/components";
import apiParticipant from "@/api/participant";
import { Websocket } from "@/helpers/websocket";
import moment from "moment";
import { i18n } from "@v2/shared/config";
import services from "@/api/services";
import { mapGetters, mapMutations } from "vuex";
import getError from "@/helpers/error";
moment.locale(i18n.locale);

export default {
  name: "ChatModal",
  data: () => ({
    popularVisible: null,
    sortBy: null,
    loading: true,
    order: null,
    statuses: [],
    message: null,
    files: [],
    socket: new Websocket(),
    isDocLoading: false,
    isMessageSending: false,
    columns: [
      {
        label: "action",
        hideDesktop: true,
        showMobile: true
      },
      {
        label: "id",
        title_rus: "№ заказа",
        title_eng: "Order №",
        showMobile: true
      },
      {
        label: "created_at",
        title_rus: "Дата",
        title_eng: "Date"
      },
      {
        label: "status",
        title_rus: "Статус",
        title_eng: "Status"
      },
      {
        label: "summ",
        title_rus: "Сумма",
        title_eng: "Sum",
        showMobile: true
      }
    ]
  }),
  components: {
    Table,
    PrimaryButton,
    Modal
  },
  computed: {
    ...mapGetters("services", ["participantOrderMessages"]),
    ...mapGetters("auth", ["userId"]),
    ...mapGetters("event", ["eventId"]),

    messages() {
      const groups = this.participantOrderMessages.reduce((groups, message) => {
        const date = message.created_at.split(" ")[0];
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(message);

        return groups;
      }, {});
      let days = Object.keys(groups).map(date => {
        return {
          date,
          messages: groups[date]
        };
      });

      days.sort((a, b) =>
        moment(a.date, "DD.MM.YYYY").toDate() >
        moment(b.date, "DD.MM.YYYY").toDate()
          ? 1
          : -1
      );
      return days;
    }
  },
  beforeDestroy() {
    this.socket.disconnectParticipantChat(this.$route.params.id);
  },
  methods: {
    ...mapMutations("services", {
      setMessages: "SET_PARTICIPANT_ORDER_MESSAGES"
    }),

    formatDate(date) {
      return moment(date, "DD.MM.YYYY HH:mm").calendar(null, {
        sameDay: `[${this.$t("services.chat.today")}]`,
        lastDay: `[${this.$t("services.chat.yesterday")}]`,
        lastWeek: "DD.MM.YYYY",
        sameElse: "DD.MM.YYYY"
      });
    },
    getPersonName(message) {
      if (message.from === "manager" && i18n.locale === "en") {
        return `${message.lastname_eng} ${message.name_eng} ${
          message.middlename_eng ? message.middlename_eng : ""
        }`;
      }
      if (message.from === "manager" && i18n.locale === "ru") {
        return `${message.manager.lastname_rus} ${message.manager.name_rus} ${
          message.manager.middlename_rus ? message.manager.middlename_rus : ""
        }`;
      }
      if (message.user?.id !== this.userId) {
        return `${message.user.lastname} ${message.user.name} ${
          message.user.middlename ? message.user.middlename : ""
        }`;
      }
      if (message.user?.id === this.userId) {
        return this.$t("services.chat.you");
      }
      return "";
    },
    displayMessage(message) {
      return message.text || message.files?.length;
    },
    getFileSize(file) {
      return file.size / 1024 >= 1000
        ? (file.size / 1024 / 1024).toFixed(1) + this.$t("services.chat.sizeMb")
        : Math.round(file.size / 1024) + this.$t("services.chat.sizeKb");
    },
    attachmentInfo(file) {
      return `${file.extension}, ${this.getFileSize(file)}`;
    },
    async downloadFile(file) {
      try {
        this.isDocLoading = true;
        const response = await services.downloadChatDocument(
          this.eventId,
          this.order.id,
          file.id
        );
        let blob = new Blob([response], {
          type: `application/${file.extension}`
        });
        const url = window.URL.createObjectURL(blob);
        let link = document.createElement("a");
        link.href = url;
        link.download = `${file.original_name}.${file.extension}`;
        link.click();
        this.isDocLoading = false;
      } catch (e) {
        this.isDocLoading = false;
      }
    },
    getTime(date) {
      let array = date.split(" ");
      if (array?.length > 1) {
        return array[1];
      }
      return "";
    },
    resizeTextArea() {
      const area = this.$refs.messageInput;
      area.style.overflowY = "hidden";
      area.style.height = area.scrollHeight + "px";
      if (area.clientHeight > 140) {
        area.style.overflowY = "scroll";
      }
      if (area.value.trim().length === 0) {
        area.style.height = 50 + "px";
        area.style.overflowY = "hidden";
      }
    },
    closeModal() {
      this.$store.commit(
        "services/SET_PARTICIPANT_ORDER_MESSAGES_READ",
        this.order.id
      );
      this.$modal.hide("chat-modal");
    },
    loadFile() {
      this.files = [...this.files, ...this.$refs.myFiles.files];
    },
    removeFile(file) {
      this.files = Array.from(this.files);
      this.files = this.files.filter(item => item.name !== file.name);
      this.$refs.myFiles.value = "";
    },
    sendMessage() {
      if (!this.message && !this.files.length) {
        this.$refs.messageInput.focus();
        return;
      }
      this.isMessageSending = true;
      let data = new FormData();
      if (this.message) {
        data.append("text", this.message);
      }
      if (this.files.length) {
        for (let i = 0; i < this.files.length; i++) {
          let file = this.files[i];
          data.append("files[]", file);
        }
      }
      apiParticipant
        .sendParticipantOrderMessages(
          this.$route.params.id,
          this.order.id,
          data
        )
        .then(data => {
          this.setMessages([
            ...this.participantOrderMessages,
            {
              from: "user",
              created_at: moment().format("DD.MM.YYYY HH:mm"),
              text: data.data.text,
              id: data.data.id,
              user: data.data.user,
              files: data.data.files
            }
          ]);
          this.message = null;
          this.files = [];
          this.scrollToBottom();
        })
        .catch(e => {
          this.$store.dispatch("common/setErrorMessage", getError(e));
        })
        .finally(() => {
          this.isMessageSending = false;
        });
    },
    getSumm(order) {
      return order.services
        .map(s => s.amount_with_vat)
        .reduce((a, b) => a + b, 0);
    },
    getStatus(id) {
      return this.statuses.find(i => i.id === id);
    },
    getStatusColor(id) {
      let cls = "status ";
      switch (id) {
        case "waiting_confirmation":
          cls += "status--orange";
          break;
        case "new":
        case "processing":
        case "confirmed":
        case "executed":
          cls += "status--blue";
          break;
        case "canceled":
        default:
          break;
      }
      return cls;
    },
    loadMessages(id) {
      this.$store
        .dispatch("services/getParticipantOrderMessages", {
          id: this.$route.params.id,
          orderId: id
        })
        .finally(() => {
          this.loading = false;
          this.scrollToBottom();
        });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messages) {
          this.$refs.messages.scrollTo(0, this.$refs.messages.scrollHeight);
        }
      });
    },
    handleKeyDown(e) {
      if (!e.shiftKey && e.keyCode === 13) {
        this.sendMessage(e);
      }
    },
    beforeOpen(event) {
      this.loading = true;
      this.socket.connectParticipantChat(this.$route.params.id, () => {
        this.loadMessages(event.params.order.id);
        apiParticipant.chatMessagesRead(
          this.$route.params.id,
          event.params.order.id
        );
      });
      this.order = event.params.order;
      this.statuses = event.params.statuses;
      if (event.params.order) {
        this.loadMessages(event.params.order.id);
        setTimeout(() => {
          apiParticipant.chatMessagesRead(
            this.$route.params.id,
            event.params.order.id
          );
        }, 2000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-modal-table {
  margin-bottom: 30px;

  &__form {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
    padding: 15px;
  }

  &__form-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;

    > span {
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 700;
      line-height: 18px;
    }
  }

  td {
    color: var(--dark);
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
  }

  &__status {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    @media (max-width: 740px) {
      justify-content: flex-end;
    }

    button {
      margin-left: 10px;
      padding: 10px 5px;
      height: 28px;
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      @media (max-width: 740px) {
        margin-left: 0;
      }
    }
  }
}

.chat-modal {
  display: flex;
  flex-direction: column;
  height: 100%;

  * {
    box-sizing: border-box;
  }

  &__title {
    padding: 30px 50px;
    font-size: 2.125rem;
    font-style: normal;
    color: var(--dark);
    font-weight: 700;
    line-height: normal;
    margin: 0;
    width: 100%;
    max-width: 940px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px solid var(--gray-darker);
    @media (max-width: 740px) {
      font-size: 1.375rem;
      font-style: normal;
      font-weight: 700;
      padding: 30px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-height: 56rem;
    }
  }

  &__body {
    width: 100%;
    max-width: 940px;
    padding: 30px 50px;
    height: calc(100vh - 111px);
    display: flex;
    flex-direction: column;
    @media (max-width: 740px) {
      padding: 30px;
      height: calc(100vh - 81px);
    }
  }
  &__attached-files {
    display: flex;
    flex-wrap: wrap;
    max-width: 830px;
    & .chat-modal__message--file {
      border-radius: 2px;
      align-items: center;
      justify-content: space-between;
      padding: 5px 10px;
      margin-right: 5px;
      margin-bottom: 5px;
      cursor: default;
      & .svg-icon--close {
        margin-right: 0;
        margin-left: 5px;
        cursor: pointer;
      }
    }
  }
  &__attach {
    & input {
      display: none;
    }
    & .svg-icon--attachment {
      width: 25px;
      height: 25px;
      cursor: pointer;
      color: #009ee3;
      margin-right: 10px;
      transition: color 0.2s;
      &:hover {
        color: #0070d2;
      }
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: auto;

    &::-webkit-scrollbar {
      width: 0;
    }
  }
  &__advice-message {
    padding: 30px 50px 10px;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    color: var(--dark);
  }
  &__divider {
    position: relative;
    width: 100%;
    height: 30px;
    display: inline-flex;
    justify-content: center;
    flex-shrink: 0;
    align-items: center;

    span {
      border-radius: 100px;
      border: 1px solid var(--gray-darker);
      padding: 5px 10px;
      color: var(--gray-dark);
      font-size: 0.625rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      position: absolute;
      z-index: 2;
      background: var(--white);
    }

    hr {
      position: relative;
      width: 100%;
      border: none;
      border-bottom: 1px solid var(--gray-darker);
    }
  }

  &__messages {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 10px;

    &--right {
      justify-content: flex-end;
    }
  }

  &__messages-avatar {
    width: 30px;
    height: 30px;
    margin-right: 10px;

    img {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      object-fit: cover;
      object-position: center;
      border-radius: 100%;
    }
  }

  &__messages-container {
    display: flex;
    flex-direction: column;
    max-width: 600px;
    width: 100%;
    padding-right: 10px;
  }

  &__messages-header {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    justify-content: flex-start;

    &--right {
      justify-content: flex-end;
    }
  }

  &__messages-name {
    color: var(--dark);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
  }

  &__messages-time {
    margin: 0 10px;
    color: var(--gray-dark);
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  &__messages-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;

    &--right {
      align-items: flex-end;
    }
  }

  &__images {
    display: flex;
    align-items: center;
    margin-top: 10px;
    flex-wrap: wrap;
  }

  &__image {
    width: 200px;
    height: 150px;
    position: relative;
    background: var(--white);
    margin-bottom: 10px;
    margin-right: 10px;
    @media (max-width: 740px) {
      width: 100px;
      height: 80px;
    }

    img {
      border-radius: 2px;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      position: relative;
    }
  }

  &__image-cover {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 2px;
    background: linear-gradient(
      0deg,
      rgba(38, 38, 38, 0.3) 0%,
      rgba(38, 38, 38, 0.3) 100%
    );
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;

    span {
      width: 50px;
      height: 50px;
      border-radius: 2px;
      background: rgba(38, 38, 38, 0.7);
      color: var(--white);
      font-size: 0.75rem;
      font-style: normal;
      font-weight: 500;
      line-height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__message {
    margin-bottom: 5px;
    border-radius: 5px;
    background-color: #009ee3;
    padding: 10px;
    width: auto;
    display: inline-flex;
    flex-direction: column;
    white-space: pre-line;

    span {
      color: var(--white);
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      @media (max-width: 740px) {
        font-size: 0.75rem;
        line-height: 18px;
      }
    }
    &--file {
      padding: 5px 15px 5px 10px;
      background: var(--gray-dark);
      display: flex;
      cursor: pointer;
      max-width: 150px;
      flex-direction: row;
      align-items: center;
      &.disabled {
        pointer-events: none;
      }
      span {
        color: #ffffff;
        text-overflow: ellipsis;
        max-width: 100px;
        overflow: hidden;
        white-space: nowrap;
        line-height: 14px;
        display: inline-block;
      }
      & .svg-icon {
        width: 16px;
        height: 16px;
        color: #ffffff;
        margin-right: 5px;
      }
      p {
        color: #ffffff;
        font-size: 10px;
        margin: 0;
        line-height: 10px;
      }
    }
    &--light {
      background: var(--main-bg);
      span {
        color: var(--dark);
      }
    }
  }

  &__message-form {
    display: flex;
    flex-direction: column;
    padding: 10px 50px 30px;
    width: 100%;
    box-sizing: border-box;
    border-top: 1px solid var(--gray-darker);
    @media (max-width: 740px) {
      padding: 15px 30px;
    }
  }

  &__cooking {
    display: inline-flex;
    align-items: center;
    margin-bottom: 10px;

    svg {
      width: 16px;
      height: 16px;
      color: var(--main);
    }
  }

  &__cooking-person {
    color: var(--dark);
    font-size: 0.625rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin: 0 5px;
  }

  &__cooking-status {
    color: var(--gray-dark);
    font-size: 0.625rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  &__form {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 830px;
  }

  &__file-input {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    width: 50px;
    flex-shrink: 0;
    input {
      opacity: 0;
      position: absolute;
    }

    svg {
      width: 20px;
      height: 20px;
      color: var(--dark-light);
    }
  }

  &__message-input {
    height: 50px;
    max-height: 148px;
    width: 100%;
    border-radius: 5px;
    resize: none;
    border: none;
    padding: 14px 15px;
    background: var(--gray);
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    box-sizing: border-box;
    transition: var(--transition);
    &__button {
      margin-left: 15px;
    }
  }

  &__send {
    margin-left: 15px;
    svg {
      width: 20px;
      height: 20px;
      color: var(--main);
    }
  }
}
</style>
